import React, { useState, useEffect } from 'react';
import treatmentService from '../../services/treatmentService';
import patientService from '../../services/patientService';

const TreatmentHistory = ({ onEditTreatment, onAddTreatment }) => {
  const [treatments, setTreatments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [expandedTreatment, setExpandedTreatment] = useState(null);

  useEffect(() => {
    fetchPatients();
    fetchTreatments();
  }, [selectedPatient]);

  const fetchPatients = async () => {
    try {
      const response = await patientService.getAllPatients({ per_page: 100 });
      setPatients(response.data.patients);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchTreatments = async () => {
    try {
      setLoading(true);
      if (selectedPatient) {
        const response = await treatmentService.getPatientTreatments(selectedPatient);
        setTreatments(response.data.treatments);
      } else {
        const response = await treatmentService.getAllTreatments({ per_page: 50 });
        setTreatments(response.data.treatments);
      }
    } catch (error) {
      console.error('Error fetching treatments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (treatment) => {
    if (!window.confirm('Delete this treatment note?')) {
      return;
    }

    try {
      await treatmentService.deleteTreatment(treatment.id);
      fetchTreatments();
    } catch (error) {
      alert('Failed to delete treatment');
    }
  };

  const getPainColor = (level) => {
    if (!level) return 'text-gray-400';
    if (level <= 3) return 'text-green-600';
    if (level <= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Filter and Add */}
      <div className="mb-6 flex flex-col md:flex-row gap-4">
        <select
          value={selectedPatient}
          onChange={(e) => setSelectedPatient(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All Patients</option>
          {patients.map((patient) => (
            <option key={patient.id} value={patient.id}>
              {patient.name}
            </option>
          ))}
        </select>

        <button
          onClick={onAddTreatment}
          className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors font-medium"
        >
          + Add Treatment Note
        </button>
      </div>

      {/* Treatment Timeline */}
      {treatments.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <div className="text-6xl mb-4">📝</div>
          <p className="text-gray-500 text-lg">No treatment notes found</p>
          <button
            onClick={onAddTreatment}
            className="mt-4 text-indigo-600 hover:text-indigo-700"
          >
            Add your first treatment note
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {treatments.map((treatment) => (
            <div
              key={treatment.id}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div
                className="p-6 cursor-pointer hover:bg-gray-50"
                onClick={() =>
                  setExpandedTreatment(
                    expandedTreatment === treatment.id ? null : treatment.id
                  )
                }
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {treatment.patient_name || 'Unknown Patient'}
                      </h3>
                      <span className="text-sm text-gray-500">
                        📅 {treatment.session_date}
                      </span>
                      {treatment.pain_level && (
                        <span className={`text-sm font-semibold ${getPainColor(treatment.pain_level)}`}>
                          Pain: {treatment.pain_level}/10
                        </span>
                      )}
                    </div>
                    <p className="text-gray-700 line-clamp-2">
                      {treatment.treatment_performed}
                    </p>
                  </div>

                  <button
                    className="text-gray-400 hover:text-gray-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedTreatment(
                        expandedTreatment === treatment.id ? null : treatment.id
                      );
                    }}
                  >
                    {expandedTreatment === treatment.id ? '▼' : '▶'}
                  </button>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedTreatment === treatment.id && (
                <div className="px-6 pb-6 border-t border-gray-200 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {treatment.observations && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-1">
                          Observations
                        </h4>
                        <p className="text-gray-600 text-sm whitespace-pre-wrap">
                          {treatment.observations}
                        </p>
                      </div>
                    )}

                    {treatment.prescribed_exercises && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-1">
                          Prescribed Exercises
                        </h4>
                        <p className="text-gray-600 text-sm whitespace-pre-wrap">
                          {treatment.prescribed_exercises}
                        </p>
                      </div>
                    )}

                    {treatment.improvement_notes && (
                      <div className="md:col-span-2">
                        <h4 className="text-sm font-semibold text-gray-700 mb-1">
                          Improvement Notes
                        </h4>
                        <p className="text-gray-600 text-sm whitespace-pre-wrap">
                          {treatment.improvement_notes}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-2 pt-4 border-t">
                    <button
                      onClick={() => onEditTreatment(treatment)}
                      className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(treatment)}
                      className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TreatmentHistory;